'use client'

import { useCallback, useMemo } from 'react'
import { useSWRConfig } from 'swr'

import { useCachedSWR } from '@/core/lib/swr/use-cached-swr'
import { serializeSwrCacheKey } from '@/core/lib/swr/swr.key'
import { creatorQuestSwrKey } from '@/features/studio-renew/lib/creator-quest-read'
import {
  creatorQuestSchema,
  type CreatorQuest,
} from '@/features/studio-renew/schemas/creator-quest.schema'

export function useCreatorQuest(epoch: string | null): {
  data: CreatorQuest | null
  error: string | null
  refresh: () => Promise<void>
  update: (value: CreatorQuest) => Promise<void>
} {
  const key = useMemo(
    () => (epoch === null ? null : creatorQuestSwrKey(epoch)),
    [epoch]
  )
  const { cache } = useSWRConfig()
  const alreadyCached =
    key !== null && cache.get(serializeSwrCacheKey(key))?.data !== undefined
  const {
    data: response,
    error,
    mutate,
  } = useCachedSWR<CreatorQuest>(key, 'REALTIME', {
    responseSchema: creatorQuestSchema,
    revalidateOnMount: alreadyCached ? false : undefined,
    revalidateOnFocus: false,
    revalidateOnReconnect: true,
  })

  const refresh = useCallback(async () => {
    if (key !== null) await mutate()
  }, [key, mutate])

  const update = useCallback(
    async (value: CreatorQuest) => {
      if (key === null) return
      const updated = await mutate(
        (previous) => {
          const current = previous ?? response
          return current?.ok ? { ...current, data: value } : current
        },
        { revalidate: false }
      )
      if (!updated) await mutate()
    },
    [key, mutate, response]
  )

  return {
    data: response?.ok ? response.data : null,
    error: error?.message ?? null,
    refresh,
    update,
  }
}
